import React, { useState, useEffect } from 'react';

const TopicForm = ({ onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    query_terms: [],
    sources: [],
    exclusion_terms: [],
    agent_intent: 'monitor',
    personal_context: {
      role: '',
      stage: '',
      goals: [],
      decisions: [],
      success_metrics: [],
      risks: [],
      timeline: '',
    },
    config: {
      frequency: '15m'
    }
  });
  const [currentGoal, setCurrentGoal] = useState('');
  const [currentDecision, setCurrentDecision] = useState('');
  const [currentSuccessMetric, setCurrentSuccessMetric] = useState('');
  const [currentRisk, setCurrentRisk] = useState('');
  const [currentQueryTerm, setCurrentQueryTerm] = useState('');
  const [currentSourceUrl, setCurrentSourceUrl] = useState('');
  const [currentExclusionTerm, setCurrentExclusionTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingMetadata, setFetchingMetadata] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (initialData) {
      // Migrate old data structure to new format
      let sources = initialData.sources || [];
      
      // Migrate from monitored_urls if present
      if (initialData.monitored_urls && initialData.monitored_urls.length > 0) {
        const migratedUrls = initialData.monitored_urls.map(url => ({
          url: url,
          metadata: {
            domain: new URL(url).hostname,
            title: new URL(url).hostname,
            type: 'website'
          }
        }));
        sources = [...sources, ...migratedUrls];
      }
      
      setFormData({
        ...formData,
        ...initialData,
        sources: sources,
        agent_intent: initialData.config?.agent_intent || initialData.agent_intent || 'monitor',
        exclusion_terms: initialData.config?.exclusion_terms || initialData.exclusion_terms || [],
        personal_context: {
          role: '',
          stage: '',
          goals: [],
          decisions: [],
          success_metrics: [],
          risks: [],
          timeline: '',
          ...(initialData.personal_context || {})
        },
        config: {
          ...formData.config,
          ...(initialData.config || {})
        }
      });
    }
    // eslint-disable-next-line
  }, [initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Intelligence agent name is required');
      return;
    }
    
    // New validation: require personal context OR traditional setup
    const hasPersonalContext = formData.personal_context.role || 
                              formData.personal_context.goals.length > 0 || 
                              formData.personal_context.decisions.length > 0;
    const hasTraditionalSetup = formData.query_terms.length > 0 || 
                               formData.sources.length > 0 || 
                               formData.description.trim();
    
    if (!hasPersonalContext && !hasTraditionalSetup) {
      setError('Tell us about your goals and decisions, OR provide keywords/sources/description');
      return;
    }

    setError('');
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        query_terms: formData.query_terms.filter(term => term.trim() !== ''),
        sources: formData.sources,
        config: {
          ...formData.config,
          thinking_mode: true,
          exclusion_terms: formData.config?.exclusion_terms || []
        }
      };
      
      await onSubmit(payload);
    } catch (err) {
      setError(err.message || (initialData ? 'Failed to update intelligence agent' : 'Failed to create intelligence agent'));
      setLoading(false);
    }
  };

  const addQueryTerm = () => {
    if (currentQueryTerm.trim()) {
      setFormData({
        ...formData,
        query_terms: [...formData.query_terms, currentQueryTerm.trim()]
      });
      setCurrentQueryTerm('');
    }
  };

  const removeQueryTerm = (index) => {
    setFormData({
      ...formData,
      query_terms: formData.query_terms.filter((_, i) => i !== index)
    });
  };

  const fetchUrlMetadata = async (url) => {
    try {
      // Extract domain and path info
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.replace('www.', '');
      
      // TODO: In the future, we could call a backend API endpoint here that fetches
      // OpenGraph tags, Twitter cards, and other rich metadata from the actual page.
      // This would provide better titles, descriptions, and higher quality images.
      // Example: const richMetadata = await sendRequest(`/api/metadata?url=${encodeURIComponent(url)}`, 'GET');
      
      // Helper function to get favicon URL with fallbacks
      const getFaviconUrl = (domain) => {
        // Try multiple favicon services in order of preference
        const faviconServices = [
          `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
          `https://icons.duckduckgo.com/ip3/${domain}.ico`,
          `https://favicon.grabify.com/${domain}`,
          `https://${domain}/favicon.ico`
        ];
        
        // For known domains with high-quality logos, use specific URLs
        const highQualityLogos = {
          'reddit.com': 'https://www.redditstatic.com/desktop2x/img/favicon/android-icon-192x192.png',
          'youtube.com': 'https://www.youtube.com/s/desktop/f7d2474d/img/favicon_144x144.png',
          'youtu.be': 'https://www.youtube.com/s/desktop/f7d2474d/img/favicon_144x144.png',
          'twitter.com': 'https://abs.twimg.com/responsive-web/client-web/icon-ios.77d25eba.png',
          'x.com': 'https://abs.twimg.com/responsive-web/client-web/icon-ios.77d25eba.png',
          'linkedin.com': 'https://static.licdn.com/scds/common/u/images/logos/favicons/v1/favicon.ico',
          'github.com': 'https://github.githubassets.com/favicons/favicon.svg',
          'medium.com': 'https://miro.medium.com/v2/1*m-R_BkNf1Qjr1YbyOIJY2w.png',
          'substack.com': 'https://substackcdn.com/image/fetch/f_auto,q_auto:good,fl_progressive:steep/https://bucketeer-e05bbc84-baa3-437e-9518-adb32be77984.s3.amazonaws.com/public/images/2cd40128-7e7e-4d5b-8f94-f9e8f0d5c0e8/apple-touch-icon-1024x1024.png',
          'news.ycombinator.com': 'https://news.ycombinator.com/y18.svg',
          'facebook.com': 'https://static.xx.fbcdn.net/rsrc.php/yb/r/hLRJ1GG_y0J.ico',
          'instagram.com': 'https://static.cdninstagram.com/rsrc.php/v3/yI/r/VsNE-OHk_8a.png',
          'tiktok.com': 'https://sf16-website-login.neutral.ttwstatic.com/obj/tiktok_web_login_static/tiktok/webapp/main/webapp-desktop/47624c235266dedd8e4d.png',
          'discord.com': 'https://discord.com/assets/847541504914fd33810e70a0ea73177e.ico',
          'slack.com': 'https://a.slack-edge.com/80588/marketing/img/icons/icon_slack_hash_colored.png',
          'notion.so': 'https://www.notion.so/images/favicon.ico',
          'spotify.com': 'https://open.spotifycdn.com/cdn/images/favicon32.a19b4f5b.png',
          'netflix.com': 'https://assets.nflxext.com/us/ffe/siteui/common/icons/nficon2016.png'
        };
        
        // Check if we have a high-quality logo for this domain
        if (highQualityLogos[domain]) {
          return highQualityLogos[domain];
        }
        
        // Return the Google favicon service as default
        return faviconServices[0];
      };
      
      // Default metadata
      let metadata = {
        domain: hostname,
        title: hostname.charAt(0).toUpperCase() + hostname.slice(1).replace(/\.[^.]+$/, ''),
        logo: getFaviconUrl(hostname),
        type: 'website',
        context: null
      };

      // Special handling for known platforms
      if (hostname.includes('reddit.com')) {
        metadata.type = 'reddit';
        metadata.title = 'Reddit';
        
        // Extract subreddit if present
        const pathParts = urlObj.pathname.split('/');
        if (pathParts[1] === 'r' && pathParts[2]) {
          metadata.context = `r/${pathParts[2]}`;
        } else if (pathParts[1] === 'u' && pathParts[2]) {
          metadata.context = `u/${pathParts[2]}`;
        }
      } else if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
        metadata.type = 'youtube';
        metadata.title = 'YouTube';
        
        // Extract channel or video info
        if (url.includes('/channel/')) {
          const channelId = url.match(/\/channel\/([^\/\?]+)/)?.[1];
          metadata.context = 'Channel';
        } else if (url.includes('/c/') || url.includes('/@')) {
          const channelName = url.match(/\/(c|@)\/([^\/\?]+)/)?.[2];
          metadata.context = channelName ? `@${channelName}` : 'Channel';
        } else if (url.includes('watch?v=')) {
          metadata.context = 'Video';
        } else if (url.includes('/playlist')) {
          metadata.context = 'Playlist';
        }
      } else if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
        metadata.type = 'twitter';
        metadata.title = 'X (Twitter)';
        
        // Extract user handle if present
        const pathParts = urlObj.pathname.split('/');
        if (pathParts[1] && !['home', 'explore', 'notifications', 'messages', 'bookmarks', 'lists'].includes(pathParts[1])) {
          metadata.context = `@${pathParts[1]}`;
        }
      } else if (hostname.includes('linkedin.com')) {
        metadata.type = 'linkedin';
        metadata.title = 'LinkedIn';
        
        // Extract company or user info
        if (url.includes('/company/')) {
          const company = url.match(/\/company\/([^\/\?]+)/)?.[1];
          metadata.context = company ? `Company: ${company}` : null;
        } else if (url.includes('/in/')) {
          const user = url.match(/\/in\/([^\/\?]+)/)?.[1];
          metadata.context = user ? `Profile: ${user}` : null;
        }
      } else if (hostname.includes('github.com')) {
        metadata.type = 'github';
        metadata.title = 'GitHub';
        
        // Extract repo info
        const pathParts = urlObj.pathname.split('/').filter(p => p);
        if (pathParts[0] && pathParts[1]) {
          metadata.context = `${pathParts[0]}/${pathParts[1]}`;
        } else if (pathParts[0]) {
          metadata.context = pathParts[0];
        }
      } else if (hostname.includes('medium.com')) {
        metadata.type = 'medium';
        metadata.title = 'Medium';
        
        // Extract publication info
        const pathParts = urlObj.pathname.split('/').filter(p => p);
        if (pathParts[0] && pathParts[0] !== 'p' && pathParts[0] !== 'tag') {
          metadata.context = `@${pathParts[0]}`;
        }
      } else if (hostname.includes('substack.com')) {
        metadata.type = 'substack';
        metadata.title = 'Substack';
        
        // Extract publication name
        const subdomain = urlObj.hostname.split('.')[0];
        if (subdomain !== 'www' && subdomain !== 'substack') {
          metadata.context = subdomain.charAt(0).toUpperCase() + subdomain.slice(1);
          metadata.title = metadata.context; // Use publication name as title
        }
      } else if (hostname.includes('news.ycombinator.com')) {
        metadata.type = 'hackernews';
        metadata.title = 'Hacker News';
      } else if (hostname.includes('stackoverflow.com')) {
        metadata.type = 'stackoverflow';
        metadata.title = 'Stack Overflow';
        
        // Extract tag info if present
        if (url.includes('/questions/tagged/')) {
          const tag = url.match(/\/tagged\/([^\/\?]+)/)?.[1];
          metadata.context = tag ? `Tag: ${tag}` : null;
        }
      } else if (hostname.includes('facebook.com') || hostname.includes('fb.com')) {
        metadata.type = 'facebook';
        metadata.title = 'Facebook';
      } else if (hostname.includes('instagram.com')) {
        metadata.type = 'instagram';
        metadata.title = 'Instagram';
        
        // Extract username
        const pathParts = urlObj.pathname.split('/').filter(p => p);
        if (pathParts[0] && pathParts[0] !== 'p' && pathParts[0] !== 'explore') {
          metadata.context = `@${pathParts[0]}`;
        }
      }

      return metadata;
    } catch (error) {
      console.error('Error fetching URL metadata:', error);
      return {
        domain: url,
        title: new URL(url).hostname || url,
        logo: 'https://www.google.com/s2/favicons?domain=' + new URL(url).hostname + '&sz=128',
        type: 'website',
        context: null
      };
    }
  };

  const addSource = async () => {
    if (!currentSourceUrl.trim()) return;
    
    setFetchingMetadata(true);
    try {
      const metadata = await fetchUrlMetadata(currentSourceUrl.trim());
      
      setFormData({
        ...formData,
        sources: [...formData.sources, { 
          url: currentSourceUrl.trim(), 
          metadata: metadata 
        }]
      });
      setCurrentSourceUrl('');
    } catch (error) {
      console.error('Error adding source:', error);
    } finally {
      setFetchingMetadata(false);
    }
  };

  const removeSource = (index) => {
    setFormData({
      ...formData,
      sources: formData.sources.filter((_, i) => i !== index)
    });
  };

  const addExclusionTerm = () => {
    if (currentExclusionTerm.trim()) {
      setFormData({
        ...formData,
        exclusion_terms: [...formData.exclusion_terms, currentExclusionTerm.trim()]
      });
      setCurrentExclusionTerm('');
    }
  };

  const removeExclusionTerm = (index) => {
    setFormData({
      ...formData,
      exclusion_terms: formData.exclusion_terms.filter((_, i) => i !== index)
    });
  };

  // Personal context handlers
  const addGoal = () => {
    if (currentGoal.trim()) {
      setFormData({
        ...formData,
        personal_context: {
          ...formData.personal_context,
          goals: [...formData.personal_context.goals, currentGoal.trim()]
        }
      });
      setCurrentGoal('');
    }
  };

  const removeGoal = (index) => {
    setFormData({
      ...formData,
      personal_context: {
        ...formData.personal_context,
        goals: formData.personal_context.goals.filter((_, i) => i !== index)
      }
    });
  };

  const addDecision = () => {
    if (currentDecision.trim()) {
      setFormData({
        ...formData,
        personal_context: {
          ...formData.personal_context,
          decisions: [...formData.personal_context.decisions, currentDecision.trim()]
        }
      });
      setCurrentDecision('');
    }
  };

  const removeDecision = (index) => {
    setFormData({
      ...formData,
      personal_context: {
        ...formData.personal_context,
        decisions: formData.personal_context.decisions.filter((_, i) => i !== index)
      }
    });
  };

  const addSuccessMetric = () => {
    if (currentSuccessMetric.trim()) {
      setFormData({
        ...formData,
        personal_context: {
          ...formData.personal_context,
          success_metrics: [...formData.personal_context.success_metrics, currentSuccessMetric.trim()]
        }
      });
      setCurrentSuccessMetric('');
    }
  };

  const removeSuccessMetric = (index) => {
    setFormData({
      ...formData,
      personal_context: {
        ...formData.personal_context,
        success_metrics: formData.personal_context.success_metrics.filter((_, i) => i !== index)
      }
    });
  };

  const addRisk = () => {
    if (currentRisk.trim()) {
      setFormData({
        ...formData,
        personal_context: {
          ...formData.personal_context,
          risks: [...formData.personal_context.risks, currentRisk.trim()]
        }
      });
      setCurrentRisk('');
    }
  };

  const removeRisk = (index) => {
    setFormData({
      ...formData,
      personal_context: {
        ...formData.personal_context,
        risks: formData.personal_context.risks.filter((_, i) => i !== index)
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          {initialData ? 'Edit Your Intelligence Agent' : 'Create Your Personal Intelligence Agent'}
        </h2>

        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <h3 className="font-medium text-purple-900 dark:text-purple-100 mb-2">🧠 Your Personal Intelligence Agent</h3>
          <p className="text-sm text-purple-800 dark:text-purple-200">
            This agent learns what matters to YOU personally. The more you use it, the better it gets at spotting opportunities, 
            risks, and connections that others miss. It builds compound knowledge around your specific goals and decisions.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-8">
          {/* Agent Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Agent Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="e.g., My Startup Intelligence Agent, Investment Opportunities Scout"
            />
          </div>

          {/* Personal Context Section */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tell Your Agent About You</h3>
            
            {/* Role & Stage */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Role
                </label>
                <select
                  value={formData.personal_context.role}
                  onChange={(e) => setFormData({
                    ...formData,
                    personal_context: { ...formData.personal_context, role: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select your role...</option>
                  <option value="founder">Founder / Entrepreneur</option>
                  <option value="investor">Investor / VC</option>
                  <option value="product_manager">Product Manager</option>
                  <option value="researcher">Researcher / Analyst</option>
                  <option value="consultant">Consultant</option>
                  <option value="executive">Executive / C-Level</option>
                  <option value="student">Student / Learning</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Stage
                </label>
                <select
                  value={formData.personal_context.stage}
                  onChange={(e) => setFormData({
                    ...formData,
                    personal_context: { ...formData.personal_context, stage: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Where are you at?</option>
                  <option value="exploring">Exploring & Learning</option>
                  <option value="evaluating">Evaluating Options</option>
                  <option value="planning">Planning & Strategy</option>
                  <option value="executing">Executing & Building</option>
                  <option value="scaling">Scaling & Growth</option>
                  <option value="exiting">Exit Planning</option>
                </select>
              </div>
            </div>

            {/* Goals */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What are your goals? 🎯
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                What do you want to achieve? Your agent will surface opportunities and insights aligned with these goals.
              </p>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={currentGoal}
                  onChange={(e) => setCurrentGoal(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addGoal())}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Find product-market fit, Raise Series A, Break into new markets"
                />
                <button
                  type="button"
                  onClick={addGoal}
                  className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.personal_context.goals.map((goal, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-sm"
                  >
                    🎯 {goal}
                    <button
                      type="button"
                      onClick={() => removeGoal(idx)}
                      className="ml-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Decisions */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What decisions do you need to make? 🤔
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Your agent will gather intelligence to help you make these decisions with confidence.
              </p>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={currentDecision}
                  onChange={(e) => setCurrentDecision(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDecision())}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Which market to enter next, Should we pivot our strategy"
                />
                <button
                  type="button"
                  onClick={addDecision}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.personal_context.decisions.map((decision, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                  >
                    🤔 {decision}
                    <button
                      type="button"
                      onClick={() => removeDecision(idx)}
                      className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Success Metrics */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                How will you measure success? 📈
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Your agent will track trends and opportunities that could impact these metrics.
              </p>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={currentSuccessMetric}
                  onChange={(e) => setCurrentSuccessMetric(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSuccessMetric())}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Revenue growth, User acquisition, Market share"
                />
                <button
                  type="button"
                  onClick={addSuccessMetric}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.personal_context.success_metrics.map((metric, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm"
                  >
                    📈 {metric}
                    <button
                      type="button"
                      onClick={() => removeSuccessMetric(idx)}
                      className="ml-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Risks */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What keeps you up at night? 😰
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Your agent will monitor for early warning signs of these risks and threats.
              </p>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={currentRisk}
                  onChange={(e) => setCurrentRisk(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRisk())}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Competitors stealing market share, Regulatory changes"
                />
                <button
                  type="button"
                  onClick={addRisk}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.personal_context.risks.map((risk, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-3 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full text-sm"
                  >
                    😰 {risk}
                    <button
                      type="button"
                      onClick={() => removeRisk(idx)}
                      className="ml-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What's your timeline?
              </label>
              <select
                value={formData.personal_context.timeline}
                onChange={(e) => setFormData({
                  ...formData,
                  personal_context: { ...formData.personal_context, timeline: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">When do you need this intelligence?</option>
                <option value="immediate">Immediate (next few days)</option>
                <option value="short_term">Short-term (next few weeks)</option>
                <option value="medium_term">Medium-term (next few months)</option>
                <option value="long_term">Long-term (next year or more)</option>
                <option value="ongoing">Ongoing monitoring</option>
              </select>
            </div>
          </div>

          {/* Agent Intelligence Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Intelligence Focus *
            </label>
            <select
              value={formData.agent_intent}
              onChange={(e) => setFormData({ ...formData, agent_intent: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="monitor">Monitor - Track developments and news</option>
              <option value="research">Research - Deep analysis and insights</option>
              <option value="trend_analysis">Trend Analysis - Identify patterns and emerging themes</option>
              <option value="competitive_intelligence">Competitive Intelligence - Track competitors and market moves</option>
              <option value="risk_assessment">Risk Assessment - Monitor potential threats and opportunities</option>
              <option value="sentiment_tracking">Sentiment Tracking - Analyze public opinion and reactions</option>
              <option value="discovery">Discovery - Find new sources and unexpected connections</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              This determines what type of intelligence patterns your agent looks for
            </p>
          </div>

          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-md">
            <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">🌱 Knowledge Compound Interest</h4>
            <p className="text-sm text-green-800 dark:text-green-200">
              The more context you provide above, the smarter your agent becomes. It learns your decision-making patterns, 
              connects dots between your interests, and builds a personal knowledge graph that gets more valuable over time.
            </p>
          </div>

          {/* Sources */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Specific Sources to Monitor <span className="text-gray-400">(optional)</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Add URLs from news sites, social media, YouTube channels, or any web source if you want to monitor specific publications.
            </p>
            <div className="flex gap-2 mb-4">
              <input
                type="url"
                value={currentSourceUrl}
                onChange={(e) => setCurrentSourceUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSource())}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                placeholder="e.g., https://reddit.com/r/technology, https://youtube.com/@channel"
                disabled={fetchingMetadata}
              />
              <button
                type="button"
                onClick={addSource}
                className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={fetchingMetadata}
              >
                {fetchingMetadata ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </>
                ) : (
                  'Add Source'
                )}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {formData.sources.map((source, idx) => (
                <div
                  key={idx}
                  className="relative group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                  style={{ opacity: 1, transition: 'opacity 0.3s ease-out' }}
                >
                  <button
                    type="button"
                    onClick={() => removeSource(idx)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center"
                  >
                    ×
                  </button>
                  
                  <div className="flex flex-col items-center text-center space-y-2">
                    <div className="w-12 h-12 relative">
                      <img 
                        src={source.metadata?.logo} 
                        alt={source.metadata.title}
                        className="w-12 h-12 rounded object-contain"
                        onError={(e) => {
                          // Try fallback favicon service
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = 'true';
                            e.target.src = `https://icons.duckduckgo.com/ip3/${source.metadata.domain}.ico`;
                          } else if (e.target.dataset.fallback === 'true') {
                            // If DuckDuckGo fails, try direct favicon
                            e.target.dataset.fallback = 'final';
                            e.target.src = `https://${source.metadata.domain}/favicon.ico`;
                          } else {
                            // Final fallback: hide image and show icon
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div 
                        className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center text-gray-500 dark:text-gray-400 absolute inset-0"
                        style={{ display: 'none' }}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m-9 9a9 9 0 019-9" />
                        </svg>
                      </div>
                    </div>
                    
                    <div className="w-full">
                      <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                        {source.metadata?.title || source.metadata?.domain || 'Website'}
                      </div>
                      {source.metadata?.context && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {source.metadata.context}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 dark:text-gray-500 truncate mt-1" title={source.url}>
                        {new URL(source.url).hostname}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {formData.sources.length === 0 && (
                <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                  No specific sources added. Your agent will use its general intelligence network.
                </div>
              )}
            </div>
          </div>

          {/* Advanced Options */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
            >
              <svg 
                className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Traditional Keywords & Filters <span className="text-gray-400">(optional)</span>
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-6">
                {/* Thinking Mode */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-purple-900 dark:text-purple-100 mb-1">
                        🧠 Experimental: Thinking Agent Mode
                      </label>
                      <p className="text-xs text-purple-700 dark:text-purple-300">
                        Enable autonomous thinking where the agent develops curiosities, forms hypotheses, 
                        and explores tangents on its own. The agent will continuously learn and evolve its understanding.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={formData.thinking_mode || false}
                        onChange={(e) => setFormData({ ...formData, thinking_mode: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                  {formData.thinking_mode && (
                    <div className="mt-3 text-xs text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/50 rounded p-2">
                      ⚡ When enabled: Agent will explore autonomously, developing its own questions and theories about your topic.
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Additional Context
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Any additional context about what you want to track and why."
                  />
                </div>

                {/* Query Terms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Specific Keywords
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={currentQueryTerm}
                      onChange={(e) => setCurrentQueryTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addQueryTerm())}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., artificial intelligence, machine learning"
                    />
                    <button
                      type="button"
                      onClick={addQueryTerm}
                      className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.query_terms.map((term, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-sm"
                      >
                        {term}
                        <button
                          type="button"
                          onClick={() => removeQueryTerm(idx)}
                          className="ml-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Exclusion Terms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Keywords to Filter Out
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={currentExclusionTerm}
                      onChange={(e) => setCurrentExclusionTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExclusionTerm())}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., spam, advertisements"
                    />
                    <button
                      type="button"
                      onClick={addExclusionTerm}
                      className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                    >
                      Exclude
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.exclusion_terms.map((term, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full text-sm"
                      >
                        {term}
                        <button
                          type="button"
                          onClick={() => removeExclusionTerm(idx)}
                          className="ml-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? (initialData ? 'Updating Agent...' : 'Creating Agent...') : (initialData ? 'Update Intelligence Agent' : 'Create Intelligence Agent')}
          </button>
        </div>
      </div>
    </form>
  );
};

export default TopicForm; 